import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  MonitorSmartphone, Loader2, CheckCircle, Save, Download, BookOpen,
  Tv, Smartphone, Box, Monitor, Wifi, AlertCircle, HelpCircle,
} from 'lucide-react';
import api from '../../services/api';

const DEVICE_ICONS = { tv: Tv, smartphone: Smartphone, box: Box, monitor: Monitor };

const blankScreen = () => ({
  hasApp: null, device_type: '', app_id: '', mac_address: '', device_id: '',
});

const DeviceSetupModal = ({ subscription, m3uLink, onClose, onSaved }) => {
  const screensCount = Math.max(1, parseInt(subscription?.screens || 1, 10));
  const [catalog, setCatalog] = useState({ devices: [], apps: [] });
  const [active, setActive] = useState(1); // écran actif
  const [forms, setForms] = useState({});
  const [savingScreen, setSavingScreen] = useState(null);
  const [savedScreens, setSavedScreens] = useState({});
  const [error, setError] = useState('');

  useEffect(() => {
    api.get('/device-app-catalog').then((r) => setCatalog(r.data || { devices: [], apps: [] })).catch(() => {});
    // Pré-remplir depuis l'existant
    const init = {};
    for (let i = 1; i <= screensCount; i++) {
      const ex = (subscription?.device_setups || []).find((s) => s.screen === i);
      init[i] = ex
        ? { hasApp: !!ex.app_id, device_type: ex.device_type || '', app_id: ex.app_id || '', mac_address: ex.mac_address || '', device_id: ex.device_id || '' }
        : blankScreen();
    }
    setForms(init);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const form = forms[active] || blankScreen();
  const setForm = (patch) => setForms((p) => ({ ...p, [active]: { ...(p[active] || blankScreen()), ...patch } }));

  const appsForDevice = useMemo(
    () => catalog.apps.filter((a) => !form.device_type || a.devices.includes(form.device_type)),
    [catalog.apps, form.device_type],
  );
  const selectedApp = useMemo(() => catalog.apps.find((a) => a.id === form.app_id) || null, [catalog.apps, form.app_id]);

  const save = async () => {
    setError('');
    setSavingScreen(active);
    try {
      const res = await api.post('/client/device-setup', {
        subscription_id: subscription.id,
        screen: active,
        device_type: form.device_type || null,
        app_id: form.hasApp ? (form.app_id || null) : null,
        mac_address: form.hasApp ? form.mac_address : '',
        device_id: form.hasApp ? form.device_id : '',
      });
      setSavedScreens((p) => ({ ...p, [active]: true }));
      if (onSaved) onSaved(res.data?.device_setups);
      setTimeout(() => setSavedScreens((p) => ({ ...p, [active]: false })), 2500);
    } catch (e) {
      setError(e.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally {
      setSavingScreen(null);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
      <motion.div
        initial={{ opacity: 0, y: 100 }} animate={{ opacity: 1, y: 0 }}
        className="bg-[#1A1A1D] rounded-t-2xl sm:rounded-lg metallic-border w-full sm:max-w-lg max-h-[92vh] overflow-y-auto p-4 sm:p-6"
        data-testid="device-setup-modal"
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-white flex items-center gap-2">
            <MonitorSmartphone className="h-5 w-5 text-[#FF6B35]" /> Configurer mes appareils
          </h3>
          <button onClick={onClose} className="text-[#6B6B6E] hover:text-white p-2" data-testid="close-device-setup">&#10005;</button>
        </div>

        <p className="text-[#B8B8BA] text-sm mb-4">Indiquez l'appareil et l'application utilisés pour <strong>chaque écran</strong> de votre abonnement. Ces informations nous aident à vous accompagner.</p>

        {/* Onglets par écran */}
        {screensCount > 1 && (
          <div className="flex gap-1 mb-4 bg-[#121214] p-1 rounded-lg overflow-x-auto" data-testid="screen-tabs">
            {Array.from({ length: screensCount }, (_, i) => i + 1).map((n) => {
              const configured = (subscription?.device_setups || []).some((s) => s.screen === n && s.device_type);
              return (
                <button key={n} onClick={() => setActive(n)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-sm font-medium whitespace-nowrap transition-all ${active === n ? 'bg-[#FF6B35] text-black' : 'text-[#B8B8BA] hover:text-white'}`}
                  data-testid={`screen-tab-${n}`}>
                  Écran {n}{configured && <CheckCircle className="h-3.5 w-3.5" />}
                </button>
              );
            })}
          </div>
        )}

        {/* Étape 1 : appareil */}
        <label className="block text-[#E8E8E8] font-medium mb-2 text-sm">1. Votre appareil</label>
        <div className="grid grid-cols-2 gap-2 mb-4" data-testid="device-grid">
          {catalog.devices.map((d) => {
            const Icon = DEVICE_ICONS[d.icon] || Tv;
            const sel = form.device_type === d.id;
            return (
              <button key={d.id} onClick={() => setForm({ device_type: d.id, app_id: '' })}
                className={`flex items-center gap-2 p-2.5 rounded-lg border text-left transition-all ${sel ? 'border-[#FF6B35] bg-[#FF6B35]/10' : 'border-[#3A3A3D] hover:border-[#6B6B6E]'}`}
                data-testid={`device-${d.id}`}>
                <Icon className={`h-4 w-4 shrink-0 ${sel ? 'text-[#FF6B35]' : 'text-[#6B6B6E]'}`} />
                <span className="text-white text-xs">{d.name}</span>
              </button>
            );
          })}
        </div>

        {form.device_type && (
          <>
            {/* Étape 2 : a-t-il déjà une appli ? */}
            <label className="block text-[#E8E8E8] font-medium mb-2 text-sm">2. Avez-vous déjà installé une application ?</label>
            <div className="flex gap-2 mb-4">
              <button onClick={() => setForm({ hasApp: true })}
                className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-all ${form.hasApp === true ? 'bg-[#00FF88] text-black' : 'bg-[#222226] text-[#B8B8BA] border border-[#3A3A3D]'}`}
                data-testid="has-app-yes">Oui</button>
              <button onClick={() => setForm({ hasApp: false })}
                className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-all ${form.hasApp === false ? 'bg-[#FF6B35] text-black' : 'bg-[#222226] text-[#B8B8BA] border border-[#3A3A3D]'}`}
                data-testid="has-app-no">Pas encore</button>
            </div>

            {/* Cas "pas encore d'appli" → guidage */}
            {form.hasApp === false && (
              <div className="bg-[#222226] rounded-lg p-4 mb-4" data-testid="no-app-guidance">
                <p className="text-white text-sm font-medium mb-2 flex items-center gap-2"><HelpCircle className="h-4 w-4 text-[#00FFFF]" /> Installons votre application</p>
                <p className="text-[#B8B8BA] text-xs mb-3">Applications recommandées pour cet appareil :</p>
                <div className="flex flex-wrap gap-1.5 mb-3">
                  {appsForDevice.map((a) => (
                    <span key={a.id} className="text-xs px-2 py-1 rounded-full bg-[#1A1A1D] text-[#FF6B35] border border-[#FF6B35]/30">{a.name}</span>
                  ))}
                </div>
                <div className="flex flex-col sm:flex-row gap-2">
                  <Link to="/telechargements" onClick={onClose} className="flex-1 flex items-center justify-center gap-2 py-2 rounded-lg bg-[#FF6B35] text-black font-semibold text-sm" data-testid="goto-downloads">
                    <Download className="h-4 w-4" /> Télécharger une app
                  </Link>
                  <Link to="/tutoriels" onClick={onClose} className="flex-1 flex items-center justify-center gap-2 py-2 rounded-lg border border-[#3A3A3D] text-white text-sm hover:border-[#FF6B35]" data-testid="goto-tutorials">
                    <BookOpen className="h-4 w-4" /> Voir le tutoriel
                  </Link>
                </div>
                <p className="text-[#6B6B6E] text-[11px] mt-3">Une fois l'application installée, revenez ici et sélectionnez « Oui » pour finaliser la configuration.</p>
              </div>
            )}

            {/* Cas "j'ai une appli" → sélection + champs conditionnels */}
            {form.hasApp === true && (
              <>
                <label className="block text-[#E8E8E8] font-medium mb-2 text-sm">3. Votre application</label>
                <select value={form.app_id} onChange={(e) => setForm({ app_id: e.target.value })}
                  className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg px-3 py-2.5 text-white text-sm focus:border-[#FF6B35] focus:outline-none mb-3"
                  data-testid="app-select">
                  <option value="">— Choisir une application —</option>
                  {appsForDevice.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>

                {selectedApp && (
                  <div className="space-y-3 mb-3" data-testid="app-fields">
                    {selectedApp.help && (
                      <p className="text-[#00FFFF] text-xs flex items-start gap-1.5"><AlertCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" />{selectedApp.help}</p>
                    )}
                    {selectedApp.requires_mac && (
                      <div>
                        <label className="block text-[#E8E8E8] text-xs mb-1">Adresse MAC</label>
                        <input value={form.mac_address} onChange={(e) => setForm({ mac_address: e.target.value })}
                          placeholder="AA:BB:CC:DD:EE:FF"
                          className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg px-3 py-2 text-white text-sm focus:border-[#FF6B35] focus:outline-none font-mono"
                          data-testid="setup-mac-input" />
                      </div>
                    )}
                    {selectedApp.requires_device_id && (
                      <div>
                        <label className="block text-[#E8E8E8] text-xs mb-1">Device ID / Device Key</label>
                        <input value={form.device_id} onChange={(e) => setForm({ device_id: e.target.value })}
                          placeholder="Identifiant affiché dans l'application"
                          className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg px-3 py-2 text-white text-sm focus:border-[#FF6B35] focus:outline-none font-mono"
                          data-testid="setup-device-id-input" />
                      </div>
                    )}
                    {selectedApp.activation === 'ibosol' && (
                      <div className="bg-[#FFB800]/10 border border-[#FFB800]/30 rounded-lg p-3" data-testid="ibosol-note">
                        <p className="text-[#FFB800] text-xs">Cette application s'active sur <strong>ibosol.org</strong> avec votre MAC + Device Key, puis ajoutez votre playlist M3U :</p>
                        {m3uLink && <p className="text-[#00FFFF] text-[11px] font-mono break-all mt-1">{m3uLink}</p>}
                      </div>
                    )}
                    {(selectedApp.activation === 'xtream' || selectedApp.activation === 'm3u') && m3uLink && (
                      <p className="text-[#6B6B6E] text-[11px] flex items-center gap-1.5"><Wifi className="h-3.5 w-3.5" /> Utilisez votre lien M3U / identifiants Xtream déjà affichés dans votre accès.</p>
                    )}
                  </div>
                )}
              </>
            )}

            {/* Enregistrer */}
            {form.hasApp !== null && (
              <>
                {error && <p className="text-[#FF3B5C] text-xs mb-2" data-testid="device-setup-error">{error}</p>}
                <button onClick={save} disabled={savingScreen === active || !form.device_type}
                  className="btn-primary w-full py-2.5 flex items-center justify-center gap-2 disabled:opacity-50"
                  data-testid="save-device-setup">
                  {savingScreen === active ? <Loader2 className="h-5 w-5 animate-spin" /> : savedScreens[active] ? <CheckCircle className="h-5 w-5" /> : <Save className="h-5 w-5" />}
                  <span>{savedScreens[active] ? 'Enregistré !' : `Enregistrer l'écran ${active}`}</span>
                </button>
              </>
            )}
          </>
        )}
      </motion.div>
    </div>
  );
};

export default DeviceSetupModal;
